import PageTitle from "../../../components/PageTitle";
import Button from "../../../components/Button";
import { useState, useEffect } from "react";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import { Icon } from "@iconify/react";
import Tabs from "@mui/lab/TabList";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TabContext from "@mui/lab/TabContext";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
// import Badge from "../../../components/Badge";
import Modal from "../../../components/Modal";
import useStyles from "../styles";
import { Grid } from "@mui/material";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { httprequest } from "../../../data/api";
import useValidator from "../../../hooks/useValidator";
import { useUserState } from "../../../context/UserContext";
import PopModal from "../../../components/PopupModal/PopModal";
import useWindowSize from "../../../hooks/useWindow";

const GET_ALL_SUBJECTS = "/api/v1/subjects";
const CLASS_ENDPOINT = "/api/v1/class";
const LESSON_NOTE = "/api/v1/lesson-notes";

const AdminLessonNote = () => {
  const navigate = useNavigate();
  const { profile } = useUserState();
  const [value, setValue] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [createNoteModal, setCreateNoteModal] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [classLevels, setClassLevels] = useState([]);
  const [isClassLoading, setIsClassLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [classValidator, showClassValidator] = useValidator();
  const [allLessonNotes, setAllLessonNotes] = useState([]);
  const [lessonNoteId, setLessonNoteId] = useState("");
  const [lessonNoteForm, setLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });
  const [editLessonNoteForm, setEditLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });
  const classes = useStyles();
  const [lessonNoteToDelete, setLessonNoteToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);

  /**
 * Gets the current window width using a custom hook.
 * Determines if the device is mobile based on screen width.
 */
  const width = useWindowSize();
  const mobile = width < 600;

  /**
 * Handles change in select input for creating a lesson note.
 * Updates the corresponding field in lessonNoteForm state.
 *
 * @param {event parameter passed on handleChange} e - The change event
 */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setLessonNoteForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
 * Handles change in select input for editing a lesson note.
 * Updates the corresponding field in editLessonNoteForm state.
 *
 * @param {event parameter passed on handleChange} e - The change event
 */
  const handleEditSelectChange = (e) => {
    const { name, value } = e.target;
    setEditLessonNoteForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
 * Handles tab change in the table.
 *
 * @param {Event} event - The event object
 * @param {number} newValue - The new tab index
 */
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  /**
 * Toggles the modal for creating a lesson note.
 */
  const handleCreateNoteModal = () => {
    setCreateNoteModal(!createNoteModal);
  };

  /**
 * Fetches all subjects from the server and updates the state.
 */
  const getAllSubjects = () => {
    httprequest
      .get(GET_ALL_SUBJECTS)
      .then((res) => {
        setAllSubjects(Array.isArray(res?.data?.data) ? res?.data?.data : []);
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Fetches all class levels from the server.
 * Sets loading state while fetching.
 */
  const getClassLevels = () => {
    setIsClassLoading(true);
    httprequest
      .get(CLASS_ENDPOINT)
      .then((res) => {
        setClassLevels(Array.isArray(res?.data?.data) ? res?.data?.data : []);
        setIsClassLoading(false);
      })
      .catch((err) => {
        setIsClassLoading(false);
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Fetches all lesson notes for the currently selected class tab.
 */
  const getAllLessonNotes = () => {
    httprequest
      .get(`${LESSON_NOTE}?class_id=${value}`)
      .then((res) => {
        const data = Array.isArray(res?.data?.data) ? res?.data?.data : [];
        setAllLessonNotes(data);
        setSearchResult(data);
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Submits the form to create a new lesson note.
 * Validates input before sending.
 */
  const handleCreateLessonNote = () => {
    if (classValidator.allValid()) {
      setIsLoading(true);
      httprequest
        .post(LESSON_NOTE, lessonNoteForm)
        .then((res) => {
          toast.success(res?.data?.message);
          getAllLessonNotes();
          handleCreateNoteModal();
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
        });
    } else {
      showClassValidator(true);
    }
  };

  /**
 * Opens the edit modal and populates it with data from the selected row.
 *
 * @param {object} row - The selected lesson note row
 */
  const handleEditModal = (row) => {
    setEditLessonNoteForm({ ...lessonNoteForm, subject_id: row?.subject_id, class_id: row?.class_id });
    setLessonNoteId(row?.id);
    setEditModal(!editModal);
  };

  /**
 * Closes the edit modal and resets the form.
 */
  const closeEditModal = () => {
    setEditLessonNoteForm({ ...lessonNoteForm, subject_id: "", class_id: "" });
    setLessonNoteId("");
    setEditModal(false);
  };

  /**
 * Submits an update request for the selected lesson note.
 *
 * @param {string} lessonNoteId - ID of the lesson note to update
 */
  const handlePatchLessonNote = (lessonNoteId) => {
    httprequest
      .patch(`${LESSON_NOTE}/${lessonNoteId}`, editLessonNoteForm)
      .then((res) => {
        toast.success(res?.data?.message);
        getAllLessonNotes();
        closeEditModal();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * Opens the delete confirmation modal for a lesson note.
 *
 * @param {string} lessonNoteId - ID of the lesson note to delete
 */
  const handleDeleteModal = (lessonNoteId) => {
    setLessonNoteToDelete(lessonNoteId);
    setDeleteModal(!deleteModal);
  };

  /**
 * Deletes the selected lesson note and updates the list.
 *
 * @param {string} lessonNoteId - ID of the lesson note to delete
 */
  const deleteLessonNote = (lessonNoteId) => {
    httprequest.delete(`${LESSON_NOTE}/${lessonNoteId}`)
      .then(() => {
        toast.success("Lesson note deleted successfully");
        setAllLessonNotes((prevNotes) => prevNotes.filter((lessonnote) => lessonnote.id !== lessonNoteId));
        setDeleteModal(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * Navigates to the view page for the selected lesson note.
 *
 * @param {object} row - The selected lesson note row
 */
  const navigateViewLesson = (row) => {
    navigate(`/lesson-notes/${row?.id}`);
  };

  /**
 * Table column definitions for the lesson note data table.
 */
  const columns = [
    {
      name: "Class",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Class</p>}
          {row?.class?.name || "--/--"}
        </>
      )
    },
    {
      name: "Subject",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Subject</p>}
          {row?.subject?.name || "--/--"}
        </>
      )
    },
    {
      name: "Note Count",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Note Count</p>}
          {row?.outline_count || "--/--"}
        </>
      )
    },
    {
      name: "Created at",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Created at</p>}
          {row?.created_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "Last Updated",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Last Updated</p>}
          {row?.updated_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button
            className="table-action-icon"
            onClick={() => navigateViewLesson(row)}
          >
            <Icon icon="fluent:eye-16-regular" />
          </button>
          <button className="table-action-icon" onClick={() => handleEditModal(row)}>
            <Icon icon="heroicons:pencil-square-solid" />
          </button>
          <button
            className="table-action-icon delete-icon"
            onClick={() => handleDeleteModal(row?.id)}
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      )
    }
  ];

  /**
 * useEffect hook to load subjects and class levels on component mount.
 */
  useEffect(() => {
    // The data will be from the Schools get api endpint
    // setSearchResult(LessonNoteData);
    getAllSubjects();
    getClassLevels();
  }, []);

  /**
 * useEffect hook to load lesson notes when selected class tab changes.
 */
  useEffect(() => {
    getAllLessonNotes();
  }, [value]);

  /**
 * Filters lesson notes based on class or subject name input.
 *
 * @param {event parameter passed on handleChange} e - The input event
 */
  const handleLessonNoteSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = allLessonNotes?.filter((data) => valArray?.every(
      (word) => data?.class?.name?.toLowerCase().includes(word.toLowerCase()) ||
      data?.subject?.name?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  /**
   * Destructure current values from lessonNoteForm
   */
  const { subject_id, class_id } = lessonNoteForm;

  return (
    <div>
      <PageTitle
        title="Lesson Note"
        subtitle="See all your Lesson Note"
        button={
          <div className="flex gap-10">
            <PopModal title="Lesson Note" videoUrl="https://youtu.be/a1BUt3ZlZBg" />
            <Button
              variant="primary"
              buttonSize="fluid"
              color="btndefault"
              onClick={() => handleCreateNoteModal()}
              buttonName="Create Lesson Note"
            />
          </div>
        }
      />
      <div className="page-content">
        <TabContext value={value}>
          <div className="tablist-container padding-left-16">
            <Tabs
              className="overide-tablist"
              onChange={handleTabChange}
              aria-label="lab API tabs example"
            >
              <Tab label="All Lesson note" value="" data={profile} />
              {isClassLoading ? (
                <div className="container-left">
                  <Icon icon="eos-icons:three-dots-loading" width="50" height="50" />
                </div>
              ) : (
                classLevels?.map((data) => {
                  return (
                    <Tab
                      key={data?.id}
                      value={data?.id}
                      label={data?.class_name}
                    />
                  );
                })
              )}
            </Tabs>
          </div>
          <>
            <TabPanel value="" className="tabpanel-overide">
              <Table
                data={searchResult}
                columns={columns}
                subHeader={true}
                pagination
                subHeaderComponent={
                  <TableSearch
                    placeholder="Search here..."
                    searchTableFunc={handleLessonNoteSearch}
                  />}
              />
            </TabPanel>
            {classLevels?.map((data) => {
              return (
                <TabPanel key={data?.id} value={data?.id}>
                  <Table
                    data={searchResult}
                    columns={columns}
                    subHeader={true}
                    pagination
                    subHeaderComponent={
                      <TableSearch
                        placeholder="Search here..."
                        searchTableFunc={handleLessonNoteSearch}
                      />
                    }
                  />
                </TabPanel>
              );
            })}
          </>
        </TabContext>
        {/* <ToastContainer /> */}
      </div>
      <Modal
        title="Create Lesson Note"
        modalContent={
          <>
            <Grid container spacing={2}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Subject"
                  required
                  children={
                    <select onChange={handleSelectChange} name="subject_id">
                      <option value="">--- Select ---</option>
                      {allSubjects?.map((subject) => {
                        return (
                          <option key={subject?.id} value={subject?.id}>
                            {subject?.name}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {classValidator.message("subject_id", subject_id, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Class"
                  required
                  children={
                    <select onChange={handleSelectChange} name="class_id">
                      <option value="">--- Select ---</option>
                      {classLevels?.map((classes) => {
                        return (
                          <option key={classes?.id} value={classes?.id}>
                            {classes?.class_name}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {classValidator.message("class_name", class_id, "required")}
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Create Lesson Note"
                onClick={handleCreateLessonNote}
                isLoading={isLoading}
              />
            </div>
          </>
        }
        isopen={createNoteModal}
        closeModal={() => {
          setCreateNoteModal(false);
        }}
      />
      {/* Edit Modal */}
      <Modal
        title="Edit Lesson Note"
        modalContent={
          <>
            <Grid container spacing={2}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Subject"
                  required
                  children={
                    <select onChange={handleEditSelectChange} name="subject_id" value={editLessonNoteForm?.subject_id}>
                      <option value="">--- Select ---</option>
                      {allSubjects?.map((subject) => {
                        return (
                          <option key={subject?.id} value={subject?.id}>
                            {subject?.name}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {classValidator.message("subject_id", subject_id, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Class"
                  required
                  children={
                    <select onChange={handleEditSelectChange} name="class_id" value={editLessonNoteForm?.class_id}>
                      <option value="">--- Select ---</option>
                      {classLevels?.map((classes) => {
                        return (
                          <option key={classes?.id} value={classes?.id}>
                            {classes?.class_name}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {classValidator.message("class_name", class_id, "required")}
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Edit Lesson Note"
                onClick={() => handlePatchLessonNote(lessonNoteId)}
                isLoading={isLoading}
              />
            </div>
          </>
        }
        isopen={editModal}
        closeModal={closeEditModal}
      />
      {/* Delete Modal */}
      <Modal
        title="Delete Lesson Note"
        subtitle="Are you sure you want to permanently delete this Lesson Note?"
        modalContent={<></>}
        modalFooter={
          <>
            <div className="flex gap-10">
              {/* <Button
                variant="dangeroutline"
                buttonSize="full"
                color="btndanger"
                buttonName="Cancel"
                onClick={handleDeleteModal}
              /> */}
              <Button
                variant="danger"
                buttonName="Delete Lesson note"
                buttonSize="full"
                color="btndefault"
                onClick={() => deleteLessonNote(lessonNoteToDelete)}
              />
            </div>
          </>
        }
        isopen={deleteModal}
        closeModal={handleDeleteModal}
      />
    </div>
  );
};

export default AdminLessonNote;
